import { useMemo } from "react";
import { useDrag } from "react-dnd";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IFlight, IGroundEvent, IAircraft } from "@vmosolutions/oc-interfaces";
import { isFlight, InteractiveState } from "src/redux/reducers/FlightTimelineReducer";
import { TimelineGL } from "src/pages/FlightTimeline/gl/TimelineGL";
import { FlightGroupChip } from "../Flights/FlightGroupChip";
import { setInteractiveState } from "src/redux/actions/flights/FlightTimeline.Actions";
import { EventGroupChip } from "../Flights/EventGroupChips";

interface ISelectedFlightsDragLayerProps {
  events: (IFlight | IGroundEvent)[]; // Filtered draggable events in the flight timeline before passed through props.
  timelineWindowId: string;
  timelineGL: TimelineGL;
  aircrafts: IAircraft[];
}

const getEventType = (events: (IFlight | IGroundEvent)[]): string => {
  return isFlight(events[0]) ? "SELECTED_FLIGHTS" : "SELECTED_GROUNDEVENTS";
};

export const SelectedFlightsDragLayer = (props: ISelectedFlightsDragLayerProps): JSX.Element => {
  const dispatch = useDispatch();

  const { events, aircrafts, timelineGL, timelineWindowId } = props;
  const interactiveState = useSelector((state: IRootState) => state.timelineFlights.interactiveState[timelineWindowId]);
  const isLocked = useMemo(() => interactiveState === InteractiveState.LockTimeline, [interactiveState]);
  const aircraftRowSetting = useSelector((state: IRootState) => state.timelineFlights.aircraftRowSetting);

  const [collected, drag, dragPreview] = useDrag(
    {
      type: getEventType(events),
      item: { selectedFlights: events, type: getEventType(events) },
      end: (item: any, monitor: any) => {
        dispatch(setInteractiveState(InteractiveState.Pan, timelineWindowId));
      },
    },
    [events, timelineWindowId]
  );

  // Fix for typescript Bug with React.KeyboardEvent (documented)
  if (!isLocked) {
    return null;
  }

  return (
    <>
      <div ref={dragPreview} style={{ position: "absolute", zIndex: -9999 }}>
        {(getEventType(events) === "SELECTED_FLIGHTS" && <FlightGroupChip data={events as IFlight[]} />) ||
          (getEventType(events) === "SELECTED_GROUNDEVENTS" && <EventGroupChip data={events as IGroundEvent[]} />)}
      </div>
      <div ref={drag} style={{ position: "absolute" }}>
        {events.map((selectedevent: IGroundEvent | IFlight) => {
          if (isFlight(selectedevent)) {
            const startX =
              timelineGL.timeAxisToPixelCoords(selectedevent.actualOut?.valueOf()) -
              aircraftRowSetting.aircraftRowPadding;
            const minX = startX > 1 ? startX : 1; // so the hover box doesn't display off the timeline.
            const maxX =
              timelineGL.timeAxisToPixelCoords(selectedevent?.actualIn?.valueOf()) +
              aircraftRowSetting.aircraftRowPadding;
            const y = timelineGL.getYPixelFromAircraft(selectedevent?.aircraft?._id, aircrafts);
            return (
              <div
                key={`${selectedevent?._id}-drag`}
                style={{
                  position: "absolute",
                  top: `${y + aircraftRowSetting?.scheduledHeight}px`,
                  left: `${minX}px`,
                  width: `${maxX - minX}px`, //`${pos.width}px`,
                  height: `${aircraftRowSetting?.aircraftRowTotalHeight - 2 * aircraftRowSetting?.scheduledHeight}px`, //`${pos.height}px`,
                  //backgroundColor: "rgba(150, 0, 0, 0.9)",
                  outline: "2px solid rgba(122, 173, 190, 1.0)",
                }}
              ></div>
            );
          } else {
            const startX =
              timelineGL.timeAxisToPixelCoords(selectedevent?.actualStart?.valueOf()) -
              aircraftRowSetting.aircraftRowPadding;
            const minX = startX > 1 ? startX : 1; // so the hover box doesn't display off the timeline.
            const maxX =
              timelineGL.timeAxisToPixelCoords(selectedevent?.actualEnd?.valueOf()) +
              aircraftRowSetting.aircraftRowPadding;
            const y = timelineGL.getYPixelFromAircraft(selectedevent?.aircraft?._id, aircrafts);
            return (
              <div
                key={`${selectedevent?._id}-drag`}
                style={{
                  position: "absolute",
                  top: `${y + aircraftRowSetting?.scheduledHeight}px`,
                  left: `${minX}px`,
                  width: `${maxX - minX}px`, //`${pos.width}px`,
                  height: `${aircraftRowSetting?.aircraftRowTotalHeight - 2 * aircraftRowSetting?.scheduledHeight}px`, //`${pos.height}px`,
                  //backgroundColor: "rgba(150, 0, 0, 0.9)",
                  outline: "2px solid rgba(122, 173, 190, 1.0)",
                }}
              ></div>
            );
          }
        })}
      </div>
    </>
  );
};
